import { toPng } from 'html-to-image';
import { getNodesBounds, getViewportForBounds } from 'reactflow';
import { clsx } from 'clsx';
import { log } from '@/logger/logger.ts';
import { POSTER_WIDTH, POSTER_HEIGHT } from '@/consts/recipe-poster';
import { ModelItem } from '@/state/nodes/nodes.types';
import { FlowMode } from '@/enums/flow-modes.enum';
import { NodeType } from '@/enums/node-type.enum';
import { RESIZABLE_NODE_TYPES } from '@/consts/node-ui.consts';
import { color } from '@/colors';
import { Connection } from './FlowComponents/FlowTour/ConnectionContext';
import type { Instance, Node as ReactFlowNode } from 'reactflow';
import type { CustomGroupData, Node } from '@/types/node';

const logger = log.getLogger('utils');

function captureVideoFrame(videoElement: HTMLVideoElement) {
  const canvas = document.createElement('canvas');
  canvas.width = videoElement.videoWidth;
  canvas.height = videoElement.videoHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error("Couldn't get canvas 2d context");
  videoElement.crossOrigin = 'anonymous';
  ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

  return canvas.toDataURL('image/png');
}

export async function createPoster({
  getNodes,
  posterWidth = POSTER_WIDTH,
  posterHeight = POSTER_HEIGHT,
}: {
  posterWidth: number;
  posterHeight: number;
  getNodes: Instance.GetNodes<ReactFlowNode>;
}) {
  const nodesBounds = getNodesBounds(getNodes());
  const transform = getViewportForBounds(nodesBounds, POSTER_WIDTH, POSTER_HEIGHT, 0.1, 3);

  const videos = document.querySelectorAll('video');
  const videoReplacements = new Map<HTMLVideoElement, HTMLImageElement>();

  videos.forEach((video) => {
    const frameDataURL = captureVideoFrame(video);
    const imageElement = new Image();
    imageElement.src = frameDataURL;
    imageElement.style.width = `${video.offsetWidth}px`;
    imageElement.style.height = `${video.offsetHeight}px`;
    if (video.parentNode) {
      video.parentNode.replaceChild(imageElement, video);
      videoReplacements.set(video, imageElement);
    }
  });

  try {
    const viewport = document.querySelector('.react-flow__viewport') as HTMLElement;
    if (!viewport) throw new Error('Viewport not found');
    const dataUrl = await toPng(viewport, {
      backgroundColor: color.Black100,
      width: posterWidth,
      height: posterHeight,
      skipFonts: true,
      style: {
        width: posterWidth.toString(),
        height: posterHeight.toString(),
        transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.zoom})`,
      },
      pixelRatio: 1,
    });

    return dataUrl;
  } catch (err) {
    logger.error('Failed to create poster image', err);
  } finally {
    videoReplacements.forEach((imageElement, video) => {
      if (!imageElement.parentNode) throw new Error('image parent not found');
      imageElement.parentNode.replaceChild(video, imageElement);
    });
  }
}

export const getOppositeHandleKeyForConnection = (
  side: Connection['handleSide'],
): keyof Pick<ModelItem, 'inputTypes' | 'outputTypes'> => (side && side === 'source' ? 'inputTypes' : 'outputTypes');

export const isRecipeInteractionAllowed = (
  isEditorOpen: boolean,
  isGalleryOpen: boolean,
  role: string,
  flowViewingMode: FlowMode,
  isMenuOpened: boolean,
) => !isEditorOpen && !isGalleryOpen && role !== 'guest' && flowViewingMode === FlowMode.Workflow && !isMenuOpened;

export function toReactFlowNode(
  node: Node,
  highlightedNodeIds: string[],
  getEffectiveIsLocked: (nodeId: string) => boolean,
  suppressedSelectionNodeId: string | null,
): ReactFlowNode {
  const effectiveIsLocked = getEffectiveIsLocked(node.id);
  const baseNode: ReactFlowNode = {
    ...node,
    draggable: effectiveIsLocked ? false : ((node as ReactFlowNode).draggable ?? undefined),
  };

  if (node.type === NodeType.CustomGroup) {
    const nodeData = node.data as CustomGroupData;
    return {
      ...baseNode,
      width: nodeData.width,
      height: nodeData.height,
      draggable: !effectiveIsLocked,
      selectable: node.id !== suppressedSelectionNodeId,
      dragHandle: '.custom-group-drag-handle',
      style: {
        width: nodeData.width,
        height: nodeData.height,
        pointerEvents: node.selected ? 'auto' : 'none', // Pass through events when not selected to allow selection box
      },
    };
  }

  if (node.type && RESIZABLE_NODE_TYPES.has(node.type as NodeType)) {
    const { width: dataWidth, height: dataHeight } = node.data as { width?: number; height?: number };
    if (dataWidth !== undefined) baseNode.width = dataWidth;
    if (dataHeight !== undefined) baseNode.height = dataHeight;
  }

  const isHighlighted = highlightedNodeIds.includes(node.id);
  return {
    ...baseNode,
    className: clsx(baseNode.className, isHighlighted && 'highlighted'),
  };
}

export const isPasteFromEditableElement = (event: ClipboardEvent): boolean => {
  // If the paste originates from an input/textarea/contenteditable element (e.g. Import node input, import model node etc.),
  // we should not treat it as a canvas paste
  const target = event.target;
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(target.closest('input, textarea, [contenteditable="true"]'));
};
